import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../config';

const API_BASE = config.api.url;

/**
 * Auth headers for the Admin Operations routes.
 *
 * Unlike the CMS client (ID token), the AdminOperations Lambda is authorized
 * in access-token mode: the gateway checks the aws.cognito.signin.user.admin
 * scope and the backend reads cognito:groups from the ACCESS token. Sending
 * the ID token here would 401.
 */
async function getAdminOpsAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
  } catch (error) {
    console.warn('Failed to get admin access token:', error);
  }
  return { 'Content-Type': 'application/json' };
}

/**
 * DM-7185: carries the HTTP status alongside the message. The email lookup has
 * to tell "no such account" (404, an answer) from "the lookup broke" (anything
 * else, not an answer), and the message text is not something to branch on.
 * Still an Error, so every existing `error.message` reader is unaffected.
 */
export class AdminOpsHttpError extends Error {
  readonly status: number;
  /**
   * DM-7976: the problem+json `code` (CLAIM_NOT_IN_STATE, BMF_GATE_FAILED, …)
   * and its `details`, so a page can map a 409/422 to an inline explanation
   * rather than branching on message text. Absent when the body had none.
   */
  readonly code?: string;
  readonly details?: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'AdminOpsHttpError';
    this.status = status;
    if (code !== undefined) this.code = code;
    if (details !== undefined) this.details = details;
  }
}

async function parseOrThrow<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new AdminOpsHttpError(
      error.detail || error.error || `HTTP ${response.status}`,
      response.status,
      typeof error.code === 'string' ? error.code : undefined,
      error.details
    );
  }
  return response.json() as Promise<T>;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered ? `?${rendered}` : '';
}

async function getJson<T>(path: string): Promise<T> {
  const headers = await getAdminOpsAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, { headers });
  return parseOrThrow<T>(response);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAdminOpsAuthHeaders();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return parseOrThrow<T>(response);
}

// ============================================================================
// Wire shapes (mirrors @donatemate/admin-operations-models + OpenAPI)
// ============================================================================

export interface ListMeta {
  next_cursor: string | null;
}

export interface ListEnvelope<T> {
  data: T[];
  meta: ListMeta;
}

export interface QueueBadge {
  count: number;
  truncated: boolean;
}

export type OverviewQueueKey =
  | 'reports_pending'
  | 'flagged_content'
  | 'irs_reviews_pending'
  | 'deletion_requests_active'
  | 'support_new'
  | 'quarantine'
  | 'outbox_failed'
  | 'avatars_stranded'
  | 'approvals_pending'
  | 'recompute_pending'
  | 'ocr_manual_review'
  | 'fraud_review';

export interface OverviewResponse {
  data: { queues: Record<OverviewQueueKey, QueueBadge> };
}

export interface ListParams {
  cursor?: string;
  limit?: number;
}

export interface ModerationReportItem {
  id: string;
  target_type: string;
  target_id: string;
  reason: string;
  description: string | null;
  status: string;
  created_at: string;
  /**
   * DM-7976: present on a report whose target is an ORGANIZATION — the
   * charity's live profile and the draft under review, so the moderator sees
   * the proposed change rather than an id. Absent on every other target.
   */
  profile_live?: ProfileFieldsWire;
  profile_draft?: ProfileFieldsWire | null;
  /**
   * DM-7976 (B1): present on a report whose target is an ORGANIZATION_IMAGE —
   * an escalated charity logo or cover held for an admin decision.
   */
  organization_image?: OrganizationImageModerationWire;
}

/** DM-7976 (B1): the held logo/cover beside the one the profile shows now. */
export interface OrganizationImageModerationWire {
  organization_id: string;
  kind: 'LOGO' | 'COVER';
  status: string;
  /** Signed media-CDN URL of the held copy; null once the image left ESCALATED. */
  view_url: string | null;
  /** The live logo_url / cover_url, or null when the profile has none. */
  live_url: string | null;
  moderation_reason: string | null;
}

export interface FlaggedContentItem {
  id: string;
  target_type: string;
  author_user_id: string;
  content_excerpt: string | null;
  moderation_status: string;
  moderation_severity: string | null;
  moderation_note: string | null;
  moderated_at: string | null;
  created_at: string;
  report_count: number;
  /** DM-7976: see ModerationReportItem.profile_live. */
  profile_live?: ProfileFieldsWire;
  profile_draft?: ProfileFieldsWire | null;
}

export type ModerationQueueView = 'reports' | 'flagged';

/** DM-7283: wire enum for POST /admin/moderation/users/{id}/resolution. */
export type UserReportResolutionAction = 'DISMISS' | 'WARN' | 'REMOVE';

export interface UserReportsResolved {
  user_id: string;
  action: UserReportResolutionAction;
  closed_report_count: number;
  /** Set when REMOVE parked a deletion for a second operator. */
  pending_action_id: string | null;
}

export interface AuditEventItem {
  id: string;
  occurred_at: string;
  category: string;
  event_type: string;
  action: string;
  outcome: string;
  reason_code: string | null;
  actor_type: string;
  actor_id: string | null;
  actor_role: string | null;
  target_type: string | null;
  target_id: string | null;
  metadata: unknown;
  diff: unknown;
}

export interface PendingActionItem {
  id: string;
  operation: string;
  payload: unknown;
  requested_by: string;
  requested_at: string;
  status: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  expires_at: string;
}

export type ApprovalDecision = 'approved' | 'rejected';

/**
 * DM-2736: what admin writes answer. Each queue's item comes only from its
 * gated read, so every mutation here invalidates and re-fetches that read
 * rather than using the write's response.
 */
export interface PendingActionDecided {
  id: string;
  operation: string;
  status: string;
  decided_at: string | null;
  expires_at: string;
}

export interface IrsReviewResolved {
  id: string;
  organization_id: string;
  resolution: string | null;
  reviewed_at: string | null;
}

export interface SupportRequestUpdated {
  id: string;
  status: string;
  reference: string | null;
  category: string | null;
  external_ref: string | null;
  first_responded_at: string | null;
  resolved_at: string | null;
  updated_at: string;
}

export interface QuarantineDecided {
  id: string;
  status: string;
}

export interface BroadcastSaved {
  id: string;
  status: string;
  audience: string;
  channels: string[];
  scheduled_for: string | null;
  cancelled_at: string | null;
  recipient_count: number;
  updated_at: string;
}

export interface PendingActionCreatedResponse {
  data: { pending_action_id: string; status: 'pending' };
}

export interface IrsSyncReviewItem {
  id: string;
  organization_id: string;
  ein: string;
  sync_job_id: string;
  reasons: string[];
  previous_values: unknown;
  proposed_values: unknown;
  reviewed_at: string | null;
  reviewed_by: string | null;
  resolution: string | null;
  resolution_notes: string | null;
  created_at: string;
}

export type IrsReviewResolution = 'approved' | 'rejected' | 'investigating';

export interface DeletionRequestItem {
  /**
   * Target for the legal-hold action. Optional until the backend adds it to
   * the list payload (known wire gap — the endpoint that consumes it exists).
   */
  id?: string;
  user_id: string;
  status: string;
  deletion_type: string | null;
  requested_at: string;
  cooling_off_until: string;
  completed_at: string | null;
  exemption_basis: string | null;
  exemption_set_by: string | null;
  exemption_set_at: string | null;
}

export type ExemptionBasis =
  'irs_recordkeeping' | 'litigation_hold' | 'regulatory_investigation' | 'contract_obligation';

export const EXEMPTION_BASES: ExemptionBasis[] = [
  'irs_recordkeeping',
  'litigation_hold',
  'regulatory_investigation',
  'contract_obligation',
];

// DM-7703: website leads (Early Access waitlist, Founding Partner pipeline)
/** DM-8485: rows per import request; the client batches a larger sheet. */
export const IMPORT_BATCH_SIZE = 100;

export type WebsiteLeadSource = 'website_form' | 'formspree_import';
export type WebsiteLeadSourceFilter = 'all' | WebsiteLeadSource;
export type WebsiteLeadConsentFilter = 'all' | 'opted_in' | 'not_opted_in';
// DM-8483: account status and how a lead may be contacted
export type WebsiteLeadAccountFilter = 'all' | 'has_account' | 'no_account';
export type WebsiteLeadContactEligibility =
  'marketing_ok' | 'launch_notice_only' | 'program_outreach_ok' | 'do_not_contact';
export type FoundingPartnerStatus = 'new' | 'contacted' | 'qualified' | 'onboarded' | 'closed';
export type FoundingPartnerStatusFilter = 'all' | FoundingPartnerStatus;

export interface WebsiteLeadConsent {
  granted: boolean;
  statement_version: string | null;
  form_version: string | null;
  recorded_at: string | null;
  source: WebsiteLeadSource | null;
}

export interface EarlyAccessLeadItem {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  donation_frequency: string | null;
  referral_source: string | null;
  referral_code: string | null;
  source_path: string;
  form_version: string;
  source: WebsiteLeadSource;
  lifecycle_status: string;
  launch_notification_expected: boolean;
  marketing_consent: WebsiteLeadConsent;
  has_account: boolean;
  account_created_at: string | null;
  contact_eligibility: WebsiteLeadContactEligibility;
  outreach: WebsiteLeadOutreachSummary;
  first_submitted_at: string;
  last_submitted_at: string;
}

export interface FoundingPartnerPipeline {
  id: string;
  workflow_status: FoundingPartnerStatus;
  contacted_at: string | null;
  qualified_at: string | null;
  onboarded_at: string | null;
  closed_at: string | null;
}

export interface FoundingPartnerRequestItem extends FoundingPartnerPipeline {
  email: string;
  first_name: string;
  last_name: string;
  organization_name: string;
  role: string;
  phone: string | null;
  organization_type: string | null;
  organization_size: string | null;
  website: string | null;
  current_donor_management: string | null;
  referral_source: string | null;
  referral_code: string | null;
  source_path: string;
  form_version: string;
  source: WebsiteLeadSource;
  marketing_consent: WebsiteLeadConsent;
  has_account: boolean;
  account_created_at: string | null;
  contact_eligibility: WebsiteLeadContactEligibility;
  outreach: WebsiteLeadOutreachSummary;
  first_submitted_at: string;
  last_submitted_at: string;
}

export interface EarlyAccessLeadsSummary {
  total: number;
  marketing_consent: number;
  formspree_import: number;
  joined_last_7_days: number;
}

export interface FoundingPartnerRequestsSummary {
  total: number;
  by_status: Record<FoundingPartnerStatus, number>;
}

/** A list page whose meta also carries a whole-table summary. */
export interface SummarizedListEnvelope<T, S> extends ListEnvelope<T> {
  meta: ListMeta & { summary: S };
}

export interface EarlyAccessLeadParams extends ListParams {
  search?: string;
  consent?: WebsiteLeadConsentFilter;
  source?: WebsiteLeadSourceFilter;
  account?: WebsiteLeadAccountFilter;
  outreach?: WebsiteLeadOutreachFilter;
}

export interface FoundingPartnerRequestParams extends ListParams {
  search?: string;
  status?: FoundingPartnerStatusFilter;
  source?: WebsiteLeadSourceFilter;
  consent?: WebsiteLeadConsentFilter;
  account?: WebsiteLeadAccountFilter;
  outreach?: WebsiteLeadOutreachFilter;
}

export interface FoundingPartnerStatusChange extends FoundingPartnerPipeline {
  changed: boolean;
}

// DM-8485: the one-to-one outreach log behind each lead. The founders email
// leads from their own Gmail (a mail merge from a Google Sheet); the console
// records what went out, what came back, and exports/imports the sheet.
export type WebsiteLeadType = 'early_access' | 'founding_partner';
export type WebsiteLeadOutreachStatus =
  'not_contacted' | 'emailed' | 'replied' | 'booked' | 'held' | 'opted_out' | 'bounced';
export type WebsiteLeadOutreachEventType =
  'email_sent' | 'replied' | 'call_booked' | 'call_held' | 'opted_out' | 'bounced' | 'note';
export type WebsiteLeadOutreachFilter =
  'all' | 'not_contacted' | 'contacted_no_reply' | 'replied' | 'booked' | 'opted_out' | 'bounced';

export interface WebsiteLeadOutreachSummary {
  status: WebsiteLeadOutreachStatus;
  emails_sent: number;
  last_event_type: WebsiteLeadOutreachEventType | null;
  /** When the newest event of any type happened. */
  last_occurred_at: string | null;
  /** When the newest email_sent happened; null until one is logged. */
  last_emailed_at: string | null;
  last_actor: string | null;
}

export interface WebsiteLeadOutreachEvent {
  id: string;
  lead_type: WebsiteLeadType;
  lead_id: string;
  event_type: WebsiteLeadOutreachEventType;
  touch: number | null;
  occurred_at: string;
  actor_id: string;
  actor_label: string | null;
  subject: string | null;
  note: string | null;
  created_at: string;
}

export interface WebsiteLeadOutreachEventsEnvelope {
  data: WebsiteLeadOutreachEvent[];
  meta: { summary: WebsiteLeadOutreachSummary };
}

export interface LogWebsiteLeadOutreachPayload {
  lead_type: WebsiteLeadType;
  event_type: WebsiteLeadOutreachEventType;
  /** ISO timestamp; the server stamps now when absent. */
  occurred_at?: string;
  /** 1..4, email_sent only. */
  touch?: number | null;
  subject?: string | null;
  note?: string | null;
  actor_label?: string | null;
}

export interface WebsiteLeadOutreachLogged {
  event: WebsiteLeadOutreachEvent;
  summary: WebsiteLeadOutreachSummary;
}

/** One stamped row of the mail-merge sheet; needs lead_id or email. */
export interface WebsiteLeadOutreachImportRow {
  lead_id?: string;
  email?: string;
  occurred_at: string;
  touch?: number | null;
  subject?: string | null;
  actor_label?: string | null;
}

export interface ImportWebsiteLeadOutreachPayload {
  lead_type: WebsiteLeadType;
  actor_label?: string | null;
  rows: WebsiteLeadOutreachImportRow[];
}

/** `mismatch`: the row carried both lead_id and email, and they named different leads. */
export type WebsiteLeadOutreachImportSkipReason =
  'no_match' | 'ambiguous' | 'not_found' | 'already_logged' | 'mismatch';

export interface WebsiteLeadOutreachImportResult {
  received: number;
  appended: number;
  /** `row` is the zero-based index into the submitted rows. */
  skipped: Array<{ row: number; reason: WebsiteLeadOutreachImportSkipReason }>;
}

export interface WebsiteLeadExport {
  filename: string;
  csv: string;
  row_count: number;
  truncated: boolean;
}

/** The export takes the list filters, never the page cursor or limit. */
export type EarlyAccessLeadExportParams = Omit<EarlyAccessLeadParams, 'cursor' | 'limit'>;
export type FoundingPartnerRequestExportParams = Omit<
  FoundingPartnerRequestParams,
  'cursor' | 'limit'
>;

export interface SupportRequestItem {
  id: string;
  user_id: string | null;
  requester_name: string | null;
  requester_email: string | null;
  source: 'app_authenticated' | 'website_support' | 'formspree_import';
  status: string;
  subject: string | null;
  message: string;
  category: string | null;
  reference: string | null;
  external_ref: string | null;
  created_at: string;
  first_responded_at: string | null;
  resolved_at: string | null;
  last_meaningful_activity_at: string | null;
}

export type SupportStatus = 'new' | 'in_review' | 'resolved';

export interface VendorDpaEntry {
  vendor_slug: string;
  dpa_status: string;
  next_review_due_at: string | null;
  last_reviewed_at: string | null;
}

export interface ComplianceRegisterResponse {
  data: {
    vendor_dpa: VendorDpaEntry[];
    kms_stale_key_count: number;
    coppa_blocked_attempts: number;
  };
}

export interface QuarantineItem {
  id: string;
  subject: string | null;
  /** Sender domain only — the backend never exposes the full address. */
  from_domain: string | null;
  status: string;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
}

export interface FailedOutboxItem {
  id: string;
  organization_id: string;
  ein: string;
  event_type: string;
  old_status: string | null;
  new_status: string;
  retry_count: number;
  max_retries: number;
  last_error: string | null;
  created_at: string;
}

export interface ReplayOutboxResponse {
  data: { replayed: number };
}

export type EnforcementAction = 'WARN' | 'SUSPEND' | 'REINSTATE';

export interface UserEnforcementResponse {
  data: {
    user_id: string;
    status: string;
    history: AuditEventItem[];
  };
}

export interface StrandedAvatarItem {
  user_id: string;
  avatar_candidate_status: string | null;
  avatar_candidate_updated_at: string | null;
}

export interface OrgStatusUnacknowledgedItem {
  donation_id: string;
  user_id: string;
  organization_id: string | null;
  amount: number | null;
  compliance_warning_reason: string | null;
  compliance_warning_set_at: string | null;
}

/** DM-5809: pre-OCR quarantine vs post-OCR fraud/redaction hold. */
export type OcrReviewHoldStatus = 'manual_review' | 'requires_review';

export interface OcrManualReviewItem {
  document_id: string;
  /** Absent from a backend that predates DM-5809; treat as manual_review. */
  processing_status?: OcrReviewHoldStatus;
  donation_id: string | null;
  user_id: string;
  document_type: string;
  file_name: string | null;
  created_at: string;
  /** When the document entered its current hold; absent from a pre-DM-5809 backend. */
  queued_at?: string;
  processing_error: string | null;
}

/**
 * DM-5804: one row per DONOR, not per flagged donation. Every velocity rule
 * describes a donor's pattern, so a donation-level list would show one
 * backfilling donor as a dozen separate alerts.
 */
export type FraudReviewSeverity = 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * DM-5802: the numbers behind one rule, captured when the donation was
 * flagged. count gifts were entered span_minutes apart (inside the rule's
 * window_minutes), with gift dates clustered within cluster_days. Historic
 * flags predate the capture and carry no findings.
 */
export interface FraudReviewFindingItem {
  code: string;
  count: number;
  window_minutes: number;
  cluster_days: number;
  span_minutes: number;
}

export interface FraudReviewDonorItem {
  user_id: string;
  /** DM-5802: null when the donor has no name on file or no longer exists. */
  display_name: string | null;
  email: string | null;
  flagged_count: number;
  severity: FraudReviewSeverity;
  findings: FraudReviewFindingItem[];
  reasons: string[];
  first_flagged_at: string;
  last_flagged_at: string;
}

/** DM-5804: one flagged donation as evidence inside a donor's case. */
export interface FraudReviewDonationItem {
  donation_id: string;
  amount_cents: number | null;
  organization_id: string | null;
  organization_name: string | null;
  reasons: string[];
  findings: FraudReviewFindingItem[];
  /** When the gift was MADE. Null when never established. */
  donation_date: string | null;
  /** When the row was ENTERED. The gap between the two is the signal. */
  created_at: string;
  /** DM-5802: null for a donation with no document (a manual entry). */
  receipt: FraudReviewReceiptItem | null;
}

/** DM-5802: the primary document; thumbnail_url is short-lived and may be null. */
export interface FraudReviewReceiptItem {
  document_id: string;
  thumbnail_url: string | null;
  expires_at: string | null;
}

/** DM-5807: another donor holding the same receipt image. */
export interface FraudReviewReceiptMatchItem {
  image_hash: string;
  donation_id: string | null;
  uploaded_at: string;
  other_user_id: string;
  other_donation_id: string | null;
  other_uploaded_at: string;
  /** Evidence, not a verdict — uploading first does not mean legitimate. */
  uploaded_first: 'this_donor' | 'other_donor';
}

export interface FraudReviewDonorProfile {
  display_name: string | null;
  email: string | null;
  status: string;
  created_at: string;
  account_age_days: number;
}

/** DM-5802: an earlier disposition on this donor, newest first. */
export interface FraudReviewPriorDecision {
  disposition: string;
  resolved_at: string;
  resolved_by: string | null;
  rationale: string | null;
  donations_resolved: number;
}

/** DM-5802: an operator OCR hold on one of the donor's documents. */
export interface FraudReviewDocumentHold {
  document_id: string;
  donation_id: string | null;
  processing_status: string;
  created_at: string;
}

export interface FraudReviewDonorDetail {
  user_id: string;
  severity: FraudReviewSeverity;
  /** Null when the case outlives a deleted account. */
  donor: FraudReviewDonorProfile | null;
  lifetime_donation_count: number;
  flagged_donation_count: number;
  donations: FraudReviewDonationItem[];
  prior_decisions: FraudReviewPriorDecision[];
  audit_timeline: AuditEventItem[];
  open_document_holds: FraudReviewDocumentHold[];
  /** Absent, not empty, when nothing collides. */
  receipt_matches?: FraudReviewReceiptMatchItem[];
}

export type FraudReviewDisposition = 'BENIGN' | 'MONITOR' | 'CONFIRMED' | 'ESCALATE';

/** DM-5805: fraud-specific, deliberately not the general enforcement enum. */
export type FraudEscalationAction = 'WARN' | 'SUSPEND' | 'UNSHARE';

export interface ResolveFraudReviewPayload {
  disposition: FraudReviewDisposition;
  rationale: string;
  escalation?: FraudEscalationAction;
  duration_days?: number;
}

export interface AuditEventParams extends ListParams {
  category?: string;
  actor_id?: string;
  target_id?: string;
}

// ============================================================================
// Client
// ============================================================================

// ============================================================================
// DM-6208 broadcasts
// ============================================================================

export type BroadcastStatus =
  | 'draft'
  | 'pending_approval'
  | 'scheduled'
  | 'expanding'
  | 'sending'
  | 'completed'
  | 'cancelled'
  | 'failed';

export interface BroadcastItem {
  id: string;
  title: string;
  body: string;
  audience: string;
  channels: string[];
  status: BroadcastStatus;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_by: string;
  approved_by: string | null;
  reason: string | null;
  recipient_count: number;
  sent_count: number;
  suppressed_count: number;
  failed_count: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface BroadcastAudiencePreview {
  audience: string;
  estimated_recipients: number;
}

export interface BroadcastTestSent {
  broadcast_id: string;
  devices_targeted: number;
  target_email: string;
}

export interface CreateBroadcastPayload {
  title: string;
  body: string;
  audience: string;
  channels?: string[];
  scheduled_for?: string;
}

export type UpdateBroadcastPayload = Partial<CreateBroadcastPayload>;

// ============================================================================
// DM-7490 partner referral codes and Founding Partner cohorts
// ============================================================================

export type ReferralCodeStatusFilter = 'all' | 'active' | 'inactive';

export interface ReferralCodeItem {
  id: string;
  code: string;
  partner_name: string;
  organization_id: string | null;
  active: boolean;
  rotated_from_code_id: string | null;
  created_at: string;
  updated_at: string;
  attributed_signups: number;
}

export interface CreateReferralCodePayload {
  code: string;
  partner_name: string;
  organization_id?: string;
}

export interface RotatedReferralCode {
  previous: ReferralCodeItem;
  code: ReferralCodeItem;
}

export type ReferralCohortGroupBy = 'partner' | 'code';

export interface ReferralCohortParams {
  from?: string;
  to?: string;
  partner_name?: string;
  code_id?: string;
  group_by?: ReferralCohortGroupBy;
}

export interface ReferralCohortCounts {
  verified_signups: number;
  deleted_accounts: number;
  activated: number;
  retention_measurable: number;
  retained: number;
  retention_not_yet_measurable: number;
}

export interface ReferralCohortRow extends ReferralCohortCounts {
  partner_name: string;
  code_id: string | null;
  code: string | null;
}

export interface ReferralCohortDefinitions {
  verified_signup: string;
  activation_window_days: number;
  retention_window_start_day: number;
  retention_window_end_day: number;
}

export interface ReferralCohortReport {
  as_of: string;
  from: string;
  to: string;
  group_by: ReferralCohortGroupBy;
  definitions: ReferralCohortDefinitions;
  rows: ReferralCohortRow[];
  totals: ReferralCohortCounts;
}

// ============================================================================
// DM-7185 account deletion
// ============================================================================

/**
 * Derived when the queue is read, never stored: EXPIRED is an unconfirmed
 * request whose emailed link lapsed; COMPLETED / CANCELLED follow the deletion
 * the confirmation opened.
 */
export type AccountDeletionWebRequestStatus =
  'PENDING_CONFIRMATION' | 'CONFIRMED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';

/** One deletion request submitted through the public website form. */
export interface AccountDeletionWebRequestItem {
  id: string;
  user_id: string;
  status: AccountDeletionWebRequestStatus;
  /** Typed by the requester. Untrusted: render as text, never as markup. */
  name: string | null;
  /** Typed by the requester. Untrusted: render as text, never as markup. */
  message: string | null;
  source: string;
  created_at: string;
  expires_at: string;
  confirmed_at: string | null;
  /** The deletion the confirmation opened; null until it is confirmed. */
  deletion_request_id: string | null;
  cooling_off_until: string | null;
}

export type UserDeletionReason = 'USER_REQUEST_EMAIL' | 'TERMS_VIOLATION' | 'OTHER';

/** Deliberately minimal: enough to open the account in the console, no more. */
export interface UserLookupResult {
  user_id: string;
  status: string;
  deleted_at: string | null;
}

export interface UserDeletionRequestSummary {
  id: string;
  status: string;
  deletion_type: string;
  requested_at: string;
  cooling_off_until: string;
  /** app | web_form | admin_console; null on requests older than DM-7185. */
  initiation_source: string | null;
  admin_reason: string | null;
  /** false for a terms-violation removal: signing in does not undo it. */
  user_cancellable: boolean;
  actor_kind: string;
}

export interface UserDeletionState {
  active: boolean;
  request: UserDeletionRequestSummary | null;
  /** Once true the deletion can no longer be cancelled by anyone. */
  phase_4_started: boolean;
}

export interface UserDeletionCancelled {
  user_id: string;
  cancelled: true;
  cancelled_at: string;
}

export type AuditLegalHoldStatus = 'active' | 'released';
export type AuditLegalHoldStatusFilter = 'all' | AuditLegalHoldStatus;
export type AuditRetentionClass = 'standard_7y' | 'extended_10y' | 'short_1y';

export interface AuditLegalHoldExtension {
  approval_ref: string;
  extended_at: string;
  added_partitions: string[];
}

export interface AuditLegalHoldItem {
  hold_id: string;
  case_id: string;
  approval_ref: string;
  partitions: string[];
  status: AuditLegalHoldStatus;
  placed_at: string;
  released_at: string | null;
  release_approval_ref: string | null;
  extensions: AuditLegalHoldExtension[];
}

export interface AuditLegalHoldParams extends ListParams {
  status?: AuditLegalHoldStatusFilter;
}

export interface PlaceAuditLegalHoldPayload {
  case_id: string;
  approval_ref: string;
  partitions: string[];
}

export interface ExtendAuditLegalHoldPayload {
  approval_ref: string;
  partitions: string[];
}

export interface ReleaseAuditLegalHoldPayload {
  approval_ref: string;
  post_release_retention_class: AuditRetentionClass;
}

export type AuditPruneApprovalStatus = 'pending' | 'expired' | 'approved' | 'rejected' | 'consumed';
export type AuditPruneApprovalStatusFilter = 'all' | 'pending' | 'decided';
export type AuditPruneApprovalDecisionRecord = 'APPROVED' | 'REJECTED';
export type AuditPruneDecision = 'APPROVE' | 'REJECT';

export interface AuditPruneApprovalItem {
  approval_id: string;
  run_id: string;
  partition_name: string;
  policy_version: string;
  verification_id: string;
  manifest_hash: string;
  authorization_id: string;
  dry_run_digest: string;
  binding_digest: string;
  requested_by: string;
  requested_at: string;
  expires_at: string;
  decision: AuditPruneApprovalDecisionRecord | null;
  decided_by: string | null;
  decision_ref: string | null;
  decided_at: string | null;
  consumed_at: string | null;
  status: AuditPruneApprovalStatus;
}

export interface AuditPruneApprovalParams extends ListParams {
  status?: AuditPruneApprovalStatusFilter;
}

export interface AuditPruneApprovalRejected {
  approval_id: string;
  decision: 'REJECTED';
  decided_at: string;
}

export interface AuditPruneDecisionReplayed {
  approval_id: string;
  decision: AuditPruneApprovalDecisionRecord;
  decided_at: string;
  replayed: true;
}

export type AuditPruneDecisionResponse =
  | PendingActionCreatedResponse
  | { data: AuditPruneApprovalRejected }
  | { data: AuditPruneDecisionReplayed };

// ============================================================================
// DM-7976 charity verification (contract v2 §4.4)
// ============================================================================

export type CharityClaimStatus =
  | 'PENDING_DOCUMENTS'
  | 'IN_REVIEW'
  | 'AWAITING_ORG_CONFIRMATION'
  | 'VERIFIED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'REVOKED';

/**
 * DISMISS_DISPUTE and CONFIRM_SAMPLE are case decisions (no status change):
 * the route admits them only when the claim's allowed_actions lists them.
 */
export type CharityVerificationDecision =
  'APPROVE' | 'REJECT' | 'REQUEST_CONFIRMATION' | 'REVOKE' | 'DISMISS_DISPUTE' | 'CONFIRM_SAMPLE';

/** Four fixed codes; NOT_ELIGIBLE never allows a resubmission. */
export type CharityRejectionReasonCode =
  'DOCUMENT_NOT_VERIFIED' | 'DOCUMENT_INVALID' | 'NOT_ELIGIBLE' | 'CONNECTION_NOT_CONFIRMED';

export type CharityRubricGate = 'PASS' | 'FAIL';
export type CharityRubricResult = CharityRubricGate | 'NOT_APPLICABLE';
export type CharityIndependentSourceType = 'FORM_990' | 'FORM_990N' | 'STATE_REGISTRY' | 'OTHER';
export type CharityContactConfirmationMethod = 'CODE' | 'PHONE' | 'LETTER_OF_AUTHORIZATION';
export type CharityContactConfirmationState = 'NONE' | 'SENT' | 'CONFIRMED' | 'LOCKED' | 'EXPIRED';
export type CharityLetterKind =
  | 'DETERMINATION_LETTER'
  | 'FORM_1023EZ_DETERMINATION'
  | 'AFFIRMATION_LETTER'
  | 'EIN_LETTER'
  | 'GROUP_EXEMPTION_LETTER'
  | 'PARENT_CONFIRMATION'
  | 'NOT_A_LETTER'
  | 'UNREADABLE';
export type CharityFlagSeverity = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type CharityCheckResult = 'PASS' | 'FLAG' | 'FAIL' | 'NOT_RUN' | 'ERROR';
export type CharityVerdictMode = 'SHADOW' | 'ENFORCED';
export type CharityAutomatedVerdict = 'APPROVE' | 'REVIEW' | 'BOUNCE';
export type CharityReviewerAgreement = 'PENDING' | 'AGREED' | 'DISAGREED';
export type CharityDocumentDecision = 'ACCEPTED' | 'REJECTED';

/** ProfileFieldsWire: a charity profile, live or draft. */
export interface ProfileFieldsWire {
  display_name: string | null;
  display_name_choice: string | null;
  category: string | null;
  mission: string | null;
  short_description: string | null;
  website_url: string | null;
  logo_url: string | null;
  cover_url: string | null;
}

export interface CharityVerificationListParams extends ListParams {
  /** Omitted: the open queue (IN_REVIEW and AWAITING_ORG_CONFIRMATION). */
  status?: CharityClaimStatus;
  /** Instead of `status`: the sampled approvals awaiting review, or the disputed claims. */
  review?: 'SAMPLE_DUE' | 'DISPUTED';
}

export interface CharityVerificationQueueOrganization {
  id: string;
  name: string;
  ein: string;
  state: string | null;
  foundation_class: string | null;
  eligibility_flags: string[];
  handle: string | null;
}

export interface CharityVerificationClaimant {
  user_id: string;
  /** "First L." — the backend never sends the claimant's full name to a list. */
  display_name: string;
  email_domain: string;
}

export interface CharityVerificationLatestVerdict {
  verdict: CharityAutomatedVerdict;
  mode: CharityVerdictMode;
  decided_at: string;
}

/** One row of GET /admin/charity/verifications: risk first, then oldest first. */
export interface CharityVerificationQueueItem {
  claim_id: string;
  status: CharityClaimStatus;
  risk_score: number;
  highest_flag_severity: CharityFlagSeverity;
  disputed: boolean;
  second_operator_required: boolean;
  approval_pending_by: string | null;
  organization: CharityVerificationQueueOrganization;
  claimant: CharityVerificationClaimant;
  submitted_at: string | null;
  age_hours: number;
  documents_count: number;
  contact_confirmation_state: CharityContactConfirmationState;
  latest_verdict: CharityVerificationLatestVerdict | null;
  sampled_for_review: boolean;
}

export interface CharityClaimContactConfirmation {
  state: CharityContactConfirmationState;
  method: CharityContactConfirmationMethod | null;
  sent_to_masked: string | null;
  sent_at: string | null;
  expires_at: string | null;
  attempts_remaining: number | null;
  resends_remaining: number | null;
  next_resend_at: string | null;
}

/** ClaimWire plus the second-operator approval fields the admin detail adds. */
export interface CharityClaimWire {
  id: string;
  status: CharityClaimStatus;
  created_at: string;
  submitted_at: string | null;
  decided_at: string | null;
  verified_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
  resubmit_deadline_at: string | null;
  rejection_reason_code: CharityRejectionReasonCode | null;
  rejection_note: string | null;
  resubmit_allowed: boolean;
  disputed: boolean;
  eligibility_flags: string[];
  contact_confirmation: CharityClaimContactConfirmation;
  verified_by_automation: boolean;
  approval_pending_by: string | null;
  approval_pending_at: string | null;
}

export interface CharityDocumentWire {
  id: string;
  slot: string;
  status: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string | null;
  bounce_reason: string | null;
  rejection_reason: string | null;
  letter_kind: CharityLetterKind | null;
  /** A 60-second presigned GET; null when there is nothing to view. */
  view_url: string | null;
  view_url_expires_at: string | null;
}

export interface CharityVerificationPub78 {
  listed: boolean;
  deductibility_codes: string[];
  list_version_date: string | null;
}

export interface CharityVerificationRevocation {
  revocation_date: string | null;
  reinstatement_date: string | null;
}

export interface CharityVerificationFiling {
  return_type: string;
  tax_period: string;
  website_domain: string | null;
  filing_date: string | null;
}

export interface CharityVerificationOrganization {
  id: string;
  name: string;
  legal_name: string;
  alternate_name: string | null;
  sort_name: string | null;
  ein: string;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  ruling_date: string | null;
  subsection: string | null;
  foundation_code: string | null;
  foundation_class: string | null;
  deductibility: string | null;
  status_code: string | null;
  affiliation_code: string | null;
  group_exemption_number: string | null;
  eligibility_flags: string[];
  handle: string | null;
  /** From the BMF, not the claimant. */
  website_url: string | null;
  pub78: CharityVerificationPub78 | null;
  revocation: CharityVerificationRevocation | null;
  filings: CharityVerificationFiling[];
}

export interface CharityVerificationAiConsent {
  status: string;
  providers_version: string | null;
}

export interface CharityVerificationClaimantDetail {
  user_id: string;
  display_name: string;
  email: string;
  email_domain: string;
  created_at: string;
  ai_consent: CharityVerificationAiConsent;
}

export interface CharityDomainRegistration {
  registered_at: string | null;
  registrar: string | null;
  rdap_status: string;
  fetched_at: string;
}

export interface CharityExtractionWire {
  document_id: string;
  provider: string;
  model: string | null;
  letter_kind: CharityLetterKind | null;
  letter_confidence: number | null;
  extracted_ein: string | null;
  extracted_name: string | null;
  extracted_ruling_date: string | null;
  extracted_subsection: string | null;
  extracted_letter_number: string | null;
  /** Keyed by extracted field name (ein, name, ruling_date, …), 0..1. */
  field_confidence: Record<string, number> | null;
  fallback_used: boolean;
  extracted_at: string;
}

export interface CharityCheckWire {
  check_type: string;
  result: CharityCheckResult;
  severity: CharityFlagSeverity;
  detail: unknown;
  checked_at: string;
  document_id: string | null;
}

export interface CharityVerdictWire {
  run_id: string;
  mode: CharityVerdictMode;
  verdict: CharityAutomatedVerdict;
  reasons: string[];
  highest_severity: CharityFlagSeverity;
  rubric: unknown;
  applied: boolean;
  sampled_for_review: boolean;
  sample_due_at: string | null;
  reviewer_agreement: CharityReviewerAgreement | null;
  decided_at: string;
}

export interface CharityDisputeWire {
  id: string;
  stage: string;
  status: string;
  details: string | null;
  created_at: string;
  resolved_at: string | null;
  /** The operator who resolved it and their note (absent until the detail serves them). */
  resolved_by?: string | null;
  resolution_note?: string | null;
}

export interface CharityVerificationContactConfirmation {
  state: CharityContactConfirmationState;
  method: CharityContactConfirmationMethod | null;
  sent_to_masked: string | null;
  sent_at: string | null;
  expires_at: string | null;
  attempts: number;
  resend_count: number;
  locked_at: string | null;
  confirmed_at: string | null;
}

/** An earlier admin decision on this claim, read from the governance ledger. */
export interface CharityVerificationPriorDecision {
  decided_at: string;
  decided_by: string;
  decision: CharityVerificationDecision;
  reason_code: CharityRejectionReasonCode | null;
  note: string | null;
  rubric: unknown;
  teos_url: string | null;
}

export interface CharityVerificationDetail {
  claim: CharityClaimWire;
  organization: CharityVerificationOrganization;
  claimant: CharityVerificationClaimantDetail;
  website_domain: string | null;
  domain_registration: CharityDomainRegistration | null;
  documents: CharityDocumentWire[];
  extractions: CharityExtractionWire[];
  /** The latest automation run. */
  checks: CharityCheckWire[];
  verdicts: CharityVerdictWire[];
  disputes: CharityDisputeWire[];
  contact_confirmation: CharityVerificationContactConfirmation;
  profile_draft: ProfileFieldsWire | null;
  profile_live: ProfileFieldsWire;
  decisions: CharityVerificationPriorDecision[];
  allowed_actions: CharityVerificationDecision[];
}

export interface CharityRubricItem {
  result: CharityRubricResult;
  notes?: string;
}

export interface CharityIndependentSource {
  type: CharityIndependentSourceType;
  value: string;
  matches_email_domain: boolean;
}

export interface CharityDecisionRubric {
  bmf_gate: CharityRubricGate;
  letter_check: CharityRubricItem;
  independent_source: CharityIndependentSource | null;
  sanctions: CharityRubricItem;
  name_comparison: { result: CharityRubricGate; notes?: string };
}

export interface CharityDocumentDecisionWire {
  document_id: string;
  status: CharityDocumentDecision;
  rejection_reason?: string;
}

export interface CharityDecisionContact {
  method: CharityContactConfirmationMethod;
  email?: string;
  phone?: string;
  note?: string;
}

/** Body of POST /admin/charity/verifications/{claimId}/decision. */
export interface CharityVerificationDecisionPayload {
  decision: CharityVerificationDecision;
  /** Required for REJECT, null otherwise. */
  reason_code: CharityRejectionReasonCode | null;
  note: string;
  /** Omitted for DISMISS_DISPUTE, which records no rubric. */
  rubric?: CharityDecisionRubric;
  documents?: CharityDocumentDecisionWire[];
  letter_kind?: CharityLetterKind;
  teos_url?: string;
  /** REJECT only; the backend forces false for NOT_ELIGIBLE. */
  resubmit_allowed?: boolean;
  /** REQUEST_CONFIRMATION, or recording a PHONE/LOA confirmation. */
  contact?: CharityDecisionContact;
}

export interface CharityVerificationDecided {
  claim: CharityClaimWire;
  notifications_queued: string[];
  /** The first of two APPROVEs: a different operator must record the second. */
  pending_second_operator: boolean;
}

/**
 * DM-2736: every charity-verification read exposes the claimant and the
 * organization's contact, so it is a privileged read made for this one
 * purpose. Stated on the request as the broadcast test does, rather than
 * left to the route's default.
 */
const CHARITY_VERIFICATION_READ_PURPOSE = {
  purpose: 'CHARITY_VERIFICATION',
  legal_basis: 'GDPR_ART6_1F_LEGITIMATE_INTEREST',
} as const;

export const adminOpsApi = {
  // Overview
  getOverview(): Promise<OverviewResponse> {
    return getJson('/admin/operations/overview');
  },

  // Moderation
  /**
   * DM-5804: donors awaiting fraud review. One row per donor, not per flagged
   * donation — the velocity rules describe a donor's pattern, so a
   * donation-level list shows one backfilling donor as a dozen alerts.
   */
  listFraudReviews(params: ListParams = {}): Promise<ListEnvelope<FraudReviewDonorItem>> {
    return getJson(`/admin/fraud-reviews${toQuery({ ...params })}`);
  },

  /** DM-5804: the flagged donations backing one donor's case. */
  getFraudReviewDonor(userId: string): Promise<{ data: FraudReviewDonorDetail }> {
    return getJson(`/admin/fraud-reviews/${encodeURIComponent(userId)}`);
  },

  /**
   * DM-5805: close a donor's case with a recorded conclusion. A rationale is
   * required for every disposition. ESCALATE returns 202 and awaits a second
   * admin's approval rather than acting.
   */
  resolveFraudReview(userId: string, payload: ResolveFraudReviewPayload) {
    return postJson(`/admin/fraud-reviews/${encodeURIComponent(userId)}/resolution`, payload);
  },

  listModerationQueue(
    view: ModerationQueueView,
    params: ListParams = {}
  ): Promise<ListEnvelope<ModerationReportItem | FlaggedContentItem>> {
    return getJson(`/admin/moderation/queue${toQuery({ view, ...params })}`);
  },

  // Moderation action (pre-existing admin endpoint, same gateway)
  moderationAction(input: {
    itemId: string;
    /** Passed through from the queue row's target_type; validated server-side. */
    targetType: string;
    targetId: string;
    action: 'APPROVE' | 'FLAG' | 'REMOVE' | 'WARN' | 'DISMISS';
    note?: string;
    /**
     * DM-6175: the author-safe violation category recorded on the removal
     * tombstone and named in the author's notification. Omitting it made every
     * removal report the generic community_guidelines reason.
     */
    violationType?: string;
  }): Promise<unknown> {
    // The moderation validator requires the path parameter to equal the body's
    // target_id, so the URL carries the CONTENT id — for report rows itemId is
    // the report id and would 400.
    return postJson(`/admin/moderation/${input.targetId}/action`, {
      target_type: input.targetType,
      target_id: input.targetId,
      action: input.action,
      ...(input.note ? { note: input.note } : {}),
      ...(input.violationType ? { violation_type: input.violationType } : {}),
    });
  },

  /**
   * DM-7283: reports whose target is a USER cannot go through the item action
   * (it refuses anything but POST/COMMENT). This closes every pending report
   * against the user with one decision; REMOVE answers 202 with a pending
   * action because it parks a TERMS_VIOLATION deletion for a second operator.
   */
  resolveUserReports(
    userId: string,
    action: UserReportResolutionAction,
    reason: string,
    note?: string
  ): Promise<{ data: UserReportsResolved }> {
    return postJson(`/admin/moderation/users/${encodeURIComponent(userId)}/resolution`, {
      action,
      reason,
      ...(note ? { note } : {}),
    });
  },

  // Audit timeline
  listAuditEvents(params: AuditEventParams = {}): Promise<ListEnvelope<AuditEventItem>> {
    return getJson(`/admin/audit-events${toQuery({ ...params })}`);
  },

  // Approvals (maker-checker)
  listApprovals(
    params: ListParams & { status?: string } = {}
  ): Promise<ListEnvelope<PendingActionItem>> {
    return getJson(`/admin/approvals${toQuery({ ...params })}`);
  },
  decideApproval(
    id: string,
    decision: ApprovalDecision,
    note?: string
  ): Promise<{ data: PendingActionDecided }> {
    return postJson(`/admin/approvals/${id}/decision`, { decision, note });
  },
  withdrawApproval(id: string): Promise<{ data: PendingActionDecided }> {
    return postJson(`/admin/approvals/${id}/withdraw`, {});
  },

  // IRS sync review
  listIrsReviews(
    params: ListParams & { pending_only?: string } = {}
  ): Promise<ListEnvelope<IrsSyncReviewItem>> {
    return getJson(`/admin/irs-sync-reviews${toQuery({ ...params })}`);
  },
  resolveIrsReview(
    id: string,
    resolution: IrsReviewResolution,
    notes?: string
  ): Promise<{ data: IrsReviewResolved }> {
    return postJson(`/admin/irs-sync-reviews/${id}/resolution`, { resolution, notes });
  },

  // Deletion requests / legal hold
  listDeletionRequests(
    params: ListParams & { status?: string } = {}
  ): Promise<ListEnvelope<DeletionRequestItem>> {
    return getJson(`/admin/deletion/requests${toQuery({ ...params })}`);
  },
  placeLegalHold(
    deletionRequestId: string,
    exemptionBasis: ExemptionBasis,
    notes?: string
  ): Promise<PendingActionCreatedResponse> {
    return postJson(`/admin/deletion/requests/${deletionRequestId}/legal-hold`, {
      exemption_basis: exemptionBasis,
      notes,
    });
  },

  // Support inbox
  listSupportRequests(
    params: ListParams & { status?: SupportStatus } = {}
  ): Promise<ListEnvelope<SupportRequestItem>> {
    return getJson(`/admin/support/requests${toQuery({ ...params })}`);
  },
  updateSupportRequest(
    id: string,
    update: { status?: SupportStatus; external_ref?: string | null }
  ): Promise<{ data: SupportRequestUpdated }> {
    return postJson(`/admin/support/requests/${id}`, update);
  },

  // DM-7703: website leads
  listEarlyAccessLeads(
    params: EarlyAccessLeadParams = {}
  ): Promise<SummarizedListEnvelope<EarlyAccessLeadItem, EarlyAccessLeadsSummary>> {
    return getJson(`/admin/website-leads/early-access${toQuery({ ...params })}`);
  },
  listFoundingPartnerRequests(
    params: FoundingPartnerRequestParams = {}
  ): Promise<SummarizedListEnvelope<FoundingPartnerRequestItem, FoundingPartnerRequestsSummary>> {
    return getJson(`/admin/website-leads/founding-partners${toQuery({ ...params })}`);
  },
  updateFoundingPartnerStatus(
    id: string,
    status: FoundingPartnerStatus
  ): Promise<{ data: FoundingPartnerStatusChange }> {
    return postJson(`/admin/website-leads/founding-partners/${encodeURIComponent(id)}/status`, {
      status,
    });
  },

  // DM-8485: outreach log, sheet export and sent-log import
  listWebsiteLeadOutreach(
    leadType: WebsiteLeadType,
    id: string
  ): Promise<WebsiteLeadOutreachEventsEnvelope> {
    return getJson(
      `/admin/website-leads/outreach/${encodeURIComponent(id)}${toQuery({ lead_type: leadType })}`
    );
  },
  logWebsiteLeadOutreach(
    id: string,
    body: LogWebsiteLeadOutreachPayload
  ): Promise<{ data: WebsiteLeadOutreachLogged }> {
    return postJson(`/admin/website-leads/outreach/${encodeURIComponent(id)}`, body);
  },
  /**
   * The route takes IMPORT_BATCH_SIZE rows per request, so a larger sheet goes
   * in sequential batches; the merged result re-indexes skips to the sheet.
   */
  async importWebsiteLeadOutreach(
    body: ImportWebsiteLeadOutreachPayload
  ): Promise<{ data: WebsiteLeadOutreachImportResult }> {
    const merged: WebsiteLeadOutreachImportResult = { received: 0, appended: 0, skipped: [] };
    for (let start = 0; start < body.rows.length; start += IMPORT_BATCH_SIZE) {
      const rows = body.rows.slice(start, start + IMPORT_BATCH_SIZE);
      const { data } = await postJson<{ data: WebsiteLeadOutreachImportResult }>(
        '/admin/website-leads/outreach/import',
        { ...body, rows }
      );
      merged.received += data.received;
      merged.appended += data.appended;
      merged.skipped.push(...data.skipped.map(skip => ({ ...skip, row: skip.row + start })));
    }
    return { data: merged };
  },
  exportEarlyAccessLeads(
    params: EarlyAccessLeadExportParams = {}
  ): Promise<{ data: WebsiteLeadExport }> {
    const { consent, account, outreach, search, source } = params;
    return getJson(
      `/admin/website-leads/early-access/export${toQuery({ consent, account, outreach, search, source })}`
    );
  },
  exportFoundingPartnerRequests(
    params: FoundingPartnerRequestExportParams = {}
  ): Promise<{ data: WebsiteLeadExport }> {
    const { status, consent, account, outreach, search, source } = params;
    return getJson(
      `/admin/website-leads/founding-partners/export${toQuery({ status, consent, account, outreach, search, source })}`
    );
  },

  // Compliance register
  getComplianceRegister(): Promise<ComplianceRegisterResponse> {
    return getJson('/admin/compliance/register');
  },
  attestVendorDpa(
    slug: string,
    nextReviewDueAt: string,
    notes?: string
  ): Promise<{ data: { attested: boolean } }> {
    return postJson('/admin/compliance/attest', {
      type: 'vendor_dpa',
      slug,
      next_review_due_at: nextReviewDueAt,
      notes,
    });
  },

  // Email quarantine
  listQuarantine(params: ListParams = {}): Promise<ListEnvelope<QuarantineItem>> {
    return getJson(`/admin/quarantine${toQuery({ ...params })}`);
  },
  decideQuarantine(id: string, reason: string): Promise<{ data: QuarantineDecided }> {
    return postJson(`/admin/quarantine/${id}/decision`, { decision: 'discard', reason });
  },

  // Failed outbox
  listFailedOutbox(params: ListParams = {}): Promise<ListEnvelope<FailedOutboxItem>> {
    return getJson(`/admin/outbox/failed${toQuery({ ...params })}`);
  },
  replayOutbox(ids: string[], reason: string): Promise<ReplayOutboxResponse> {
    return postJson('/admin/outbox/replay', { ids, reason });
  },
  replayAllOutbox(reason: string): Promise<PendingActionCreatedResponse> {
    return postJson('/admin/outbox/replay', { all: true, reason });
  },

  // User enforcement
  getUserEnforcement(userId: string): Promise<UserEnforcementResponse> {
    return getJson(`/admin/users/${userId}/enforcement`);
  },
  enforceUser(
    userId: string,
    action: EnforcementAction,
    reason: string,
    durationDays?: number
  ): Promise<PendingActionCreatedResponse | { data: unknown }> {
    return postJson(`/admin/users/${userId}/enforcement`, {
      action,
      reason,
      duration_days: durationDays,
    });
  },

  // Stranded avatars
  listStrandedAvatars(
    params: ListParams & { older_than_minutes?: number } = {}
  ): Promise<ListEnvelope<StrandedAvatarItem>> {
    return getJson(`/admin/avatars/stranded${toQuery({ ...params })}`);
  },

  // FM-56 backlog
  listOrgStatusUnacknowledged(
    params: ListParams = {}
  ): Promise<ListEnvelope<OrgStatusUnacknowledgedItem>> {
    return getJson(`/admin/org-status/unacknowledged${toQuery({ ...params })}`);
  },

  // OCR manual review (existing FM-21 endpoints on the same gateway)
  listOcrManualReview(params: ListParams = {}): Promise<ListEnvelope<OcrManualReviewItem>> {
    return getJson(`/admin/ocr/manual-review${toQuery({ ...params })}`);
  },
  decideOcrManualReview(
    documentId: string,
    action: 'approve' | 'reject',
    note?: string,
    expectedStatus?: OcrReviewHoldStatus
  ): Promise<{ data: unknown }> {
    // DM-5809: the hold kind the reviewer saw; the backend refuses with 409 if
    // the document has since moved to another kind of hold.
    return postJson(`/admin/ocr/manual-review/${documentId}/decision`, {
      action,
      note,
      expected_status: expectedStatus,
    });
  },

  // DM-6208 broadcasts. Every mutation is POST: the admin route table admits
  // only GET and POST, and this client has no PUT/DELETE helper.
  listBroadcasts(params: ListParams & { status?: string } = {}) {
    return getJson<ListEnvelope<BroadcastItem>>(`/admin/broadcasts${toQuery({ ...params })}`);
  },

  getBroadcast(id: string) {
    return getJson<{ data: BroadcastItem }>(`/admin/broadcasts/${encodeURIComponent(id)}`);
  },

  /**
   * An estimate, not a promise: the audience is snapshotted when expansion
   * runs, so anyone created between this call and the send is not included.
   */
  previewBroadcastAudience(audience: string) {
    return getJson<{ data: BroadcastAudiencePreview }>(
      `/admin/broadcasts/audience-preview${toQuery({ audience })}`
    );
  },

  createBroadcast(payload: CreateBroadcastPayload) {
    return postJson<{ data: BroadcastSaved }>('/admin/broadcasts', payload);
  },

  updateBroadcast(id: string, payload: UpdateBroadcastPayload) {
    return postJson<{ data: BroadcastSaved }>(
      `/admin/broadcasts/${encodeURIComponent(id)}/content`,
      payload
    );
  },

  /**
   * Sends to one named app account's devices.
   *
   * Not "the operator's own devices": admin sign-in is a different Cognito
   * pool from app accounts, so an operator's own identity is not a user the
   * notification stack can address.
   */
  testBroadcast(id: string, targetEmail: string) {
    // DM-2736: resolving an email to an app account is a privileged read, and
    // this route never defaults its purpose -- the case is the broadcast under test.
    const purpose = toQuery({
      purpose: 'SUPPORT_CASE_HANDLING',
      legal_basis: 'GDPR_ART6_1F_LEGITIMATE_INTEREST',
      case_id: id,
    });
    return postJson<{ data: BroadcastTestSent }>(
      `/admin/broadcasts/${encodeURIComponent(id)}/test${purpose}`,
      { target_email: targetEmail }
    );
  },

  /** Returns 202 and a pending action; a second operator must approve it. */
  requestBroadcastApproval(id: string, reason: string) {
    return postJson<PendingActionCreatedResponse>(
      `/admin/broadcasts/${encodeURIComponent(id)}/approval-request`,
      { reason }
    );
  },

  /** Not dual-control gated: stopping a send must not wait on a second operator. */
  cancelBroadcast(id: string) {
    return postJson<{ data: BroadcastSaved }>(
      `/admin/broadcasts/${encodeURIComponent(id)}/cancellation`,
      {}
    );
  },

  // DM-7490 referral codes. Every mutation is POST for the same reason as the
  // broadcasts: the admin route table admits only GET and POST.
  async listReferralCodes(status: ReferralCodeStatusFilter = 'all') {
    const body = await getJson<{ data: { codes: ReferralCodeItem[] } }>(
      `/admin/referrals/codes${toQuery({ status: status === 'all' ? undefined : status })}`
    );
    return body.data;
  },

  async createReferralCode(payload: CreateReferralCodePayload) {
    const body = await postJson<{ data: { code: ReferralCodeItem } }>(
      '/admin/referrals/codes',
      payload
    );
    return body.data;
  },

  async setReferralCodeStatus(id: string, active: boolean, reason: string) {
    const body = await postJson<{ data: { code: ReferralCodeItem } }>(
      `/admin/referrals/codes/${encodeURIComponent(id)}/status`,
      { active, reason }
    );
    return body.data;
  },

  async rotateReferralCode(id: string, newCode: string, reason: string) {
    const body = await postJson<{ data: RotatedReferralCode }>(
      `/admin/referrals/codes/${encodeURIComponent(id)}/rotate`,
      { new_code: newCode, reason }
    );
    return body.data;
  },

  async getReferralCohorts(params: ReferralCohortParams = {}) {
    const body = await getJson<{ data: ReferralCohortReport }>(
      `/admin/referrals/cohorts${toQuery({ ...params })}`
    );
    return body.data;
  },

  // DM-7185 account deletion. The cancellation is POST-shaped for the reason
  // the broadcast one is: this client (and the route table) has no DELETE.
  listAccountDeletionRequests(
    params: ListParams & { status?: AccountDeletionWebRequestStatus } = {}
  ) {
    return getJson<ListEnvelope<AccountDeletionWebRequestItem>>(
      `/admin/account-deletion-requests${toQuery({ ...params })}`
    );
  },

  /**
   * Resolves an APP account's sign-in address. An operator's admin address
   * will 404: admin sign-in is a different Cognito pool from app accounts.
   * Every call, hit or miss, is written to the audit ledger server-side.
   */
  lookupUserByEmail(email: string) {
    return getJson<{ data: UserLookupResult }>(`/admin/users/lookup${toQuery({ email })}`);
  },

  /** 404s for an unknown user id, so "nothing scheduled" is always a real answer. */
  getUserDeletion(userId: string) {
    return getJson<{ data: UserDeletionState }>(
      `/admin/users/${encodeURIComponent(userId)}/deletion`
    );
  },

  /** Returns 202 and a pending action; a second operator must approve it. */
  requestUserDeletion(userId: string, reason: UserDeletionReason, note: string) {
    return postJson<PendingActionCreatedResponse>(
      `/admin/users/${encodeURIComponent(userId)}/deletion`,
      { reason, note }
    );
  },

  /** Not dual-control gated: stopping a deletion must not wait on a second operator. */
  cancelUserDeletion(userId: string, note: string) {
    return postJson<{ data: UserDeletionCancelled }>(
      `/admin/users/${encodeURIComponent(userId)}/deletion/cancellation`,
      { note }
    );
  },

  listAuditLegalHolds({ status, ...params }: AuditLegalHoldParams = {}) {
    return getJson<ListEnvelope<AuditLegalHoldItem>>(
      `/admin/audit-retention/legal-holds${toQuery({
        status: status === 'all' ? undefined : status,
        ...params,
      })}`
    );
  },

  placeAuditLegalHold(payload: PlaceAuditLegalHoldPayload) {
    return postJson<PendingActionCreatedResponse>('/admin/audit-retention/legal-holds', payload);
  },

  extendAuditLegalHold(holdId: string, payload: ExtendAuditLegalHoldPayload) {
    return postJson<PendingActionCreatedResponse>(
      `/admin/audit-retention/legal-holds/${encodeURIComponent(holdId)}/extend`,
      payload
    );
  },

  releaseAuditLegalHold(holdId: string, payload: ReleaseAuditLegalHoldPayload) {
    return postJson<PendingActionCreatedResponse>(
      `/admin/audit-retention/legal-holds/${encodeURIComponent(holdId)}/release`,
      payload
    );
  },

  listAuditPruneApprovals({ status, ...params }: AuditPruneApprovalParams = {}) {
    return getJson<ListEnvelope<AuditPruneApprovalItem>>(
      `/admin/audit-retention/prune-approvals${toQuery({
        status: status === 'all' ? undefined : status,
        ...params,
      })}`
    );
  },

  decideAuditPruneApproval(approvalId: string, decision: AuditPruneDecision, decisionRef: string) {
    return postJson<AuditPruneDecisionResponse>(
      `/admin/audit-retention/prune-approvals/${encodeURIComponent(approvalId)}/decision`,
      { decision, decision_ref: decisionRef }
    );
  },

  // DM-7976 charity verification. The queue is ordered by the backend: risk
  // score first, then oldest submission first; the client never re-sorts.
  listCharityVerifications(params: CharityVerificationListParams = {}) {
    return getJson<ListEnvelope<CharityVerificationQueueItem>>(
      `/admin/charity/verifications${toQuery({ ...params, ...CHARITY_VERIFICATION_READ_PURPOSE })}`
    );
  },

  /**
   * The claim's full evidence. Every document link in the answer is a
   * 60-second presigned GET, so a drawer left open re-fetches rather than
   * reusing a lapsed one. The case under the read is the claim itself.
   */
  getCharityVerification(claimId: string) {
    return getJson<{ data: CharityVerificationDetail }>(
      `/admin/charity/verifications/${encodeURIComponent(claimId)}${toQuery({
        ...CHARITY_VERIFICATION_READ_PURPOSE,
        case_id: claimId,
      })}`
    );
  },

  /**
   * Records one decision. 409 CLAIM_NOT_IN_STATE (details carry the current
   * status and allowed_actions), SECOND_OPERATOR_REQUIRED and CLAIM_DISPUTED,
   * and 422 INDEPENDENT_SOURCE_REQUIRED / BMF_GATE_FAILED, are answers the
   * page explains inline — see charityVerification.logic.ts.
   */
  decideCharityVerification(claimId: string, payload: CharityVerificationDecisionPayload) {
    return postJson<{ data: CharityVerificationDecided }>(
      `/admin/charity/verifications/${encodeURIComponent(claimId)}/decision`,
      payload
    );
  },
};
